'use client';
import React, { useEffect, useRef } from 'react';

const tiers = [
  {
    id: 'premium',
    name: 'Premium Detailing',
    tagline: 'Complete interior clean',
    price: '$230',
    priceNote: '',
    featured: false,
    includes: [
      'General Clean',
      'Full Vacuum',
      'Shampoo Floor Mats',
      'Scrub & Shine Inside Door Panel',
      'Scrub & Shine Dashboard and Console Vinyl',
      'Clean Glass',
      'General Clean of Seats',
      'Clean and Vacuum Trunk',
      'Dashboard and Console Protection',
      'Air Freshener Application',
    ],
    cta: 'Book Premium',
    badge: null,
  },
  {
    id: 'executive',
    name: 'Executive Detailing',
    tagline: 'The ultimate detailing experience',
    price: '$280',
    priceNote: '',
    featured: true,
    includes: [
      'Full Detailed Vacuum',
      'Air Freshener Application',
      'Shampoo Floor Mats',
      'Scrub & Shine Doors, Dashboard & Console',
      'Deep Cleaning of Car Flooring',
      'Steam Clean All Fabric',
      'Clean & Condition Leather',
      'Detailed Trunk Cleaning & Vacuum',
      'UV Protection for Dashboard and Console',
    ],
    cta: 'Book Executive',
    badge: 'Most Popular',
  },
];

const additionalServices = [
  { name: 'Pet Hair Removal', price: '$30' },
  { name: 'Stubborn Stain Removal', price: '$30–$70', note: '$30 for Executive Package' },
  { name: 'Salt Stains Removal', price: '$30–$60', note: '$30 for Executive Package' },
  { name: 'Fabric Protection', price: '$30' },
  { name: 'Leather Protection', price: '$30' },
  { name: 'Fabric & Leather Protection', price: '$40', note: 'Executive Package only' },
];

const byDemandServices = [
  'Clay Bar Treatment',
  'Headlight Restoration',
  'Engine Bay Wash and Shine',
  'Trim and Plastic Restoration',
  'Headliner Detailing',
  'Waxing',
];

export default function ServiceTiersSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32" style={{ background: 'var(--cockpit-black)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="metric-pill mb-5 reveal">Detailing Packages</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '-0.03em' }}
          >
            Choose your package<br />
            <span style={{ color: 'var(--aluminum-dim)' }}>of precision.</span>
          </h2>
          <p className="mt-4 max-w-lg mx-auto reveal reveal-delay-2" style={{ color: 'var(--aluminum-dim)', fontSize: '16px' }}>
            White-glove pickup & drop-off available. Serving Riverview & Moncton.
          </p>
        </div>

        {/* Tier cards - 2 columns centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {tiers?.map((tier, i) => (
            <div
              key={tier?.id}
              className={`tier-card reveal reveal-delay-${i + 1} flex flex-col ${tier?.featured ? 'featured' : ''}`}
              style={{ transform: tier?.featured ? 'scale(1.03)' : undefined }}
            >
              {/* Top accent */}
              {tier?.featured && (
                <div
                  className="h-1 w-full"
                  style={{ background: 'linear-gradient(to right, var(--ceramic-blue), rgba(59,130,246,0.3))' }}
                />
              )}
              <div className="p-8 flex flex-col flex-1">
                {/* Badge */}
                {tier?.badge && (
                  <div className="mb-4">
                    <span
                      style={{
                        fontSize: '11px',
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: tier?.featured ? 'var(--ceramic-blue)' : 'var(--aluminum-dim)',
                        background: tier?.featured ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${tier?.featured ? 'rgba(59,130,246,0.3)' : 'rgba(63,63,70,0.8)'}`,
                        borderRadius: '9999px',
                        padding: '4px 12px',
                        display: 'inline-block',
                      }}
                    >
                      {tier?.badge}
                    </span>
                  </div>
                )}

                <h3
                  className="font-manrope font-extrabold text-white mb-1"
                  style={{ fontSize: '22px', letterSpacing: '-0.02em' }}
                >
                  {tier?.name}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--aluminum-dim)', marginBottom: '24px' }}>
                  {tier?.tagline}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <span
                    className="font-manrope font-extrabold text-white"
                    style={{ fontSize: '40px', letterSpacing: '-0.04em', lineHeight: 1 }}
                  >
                    {tier?.price}
                  </span>
                  {tier?.priceNote && (
                    <span style={{ fontSize: '13px', color: 'var(--aluminum-dim)', marginLeft: '8px' }}>
                      {tier?.priceNote}
                    </span>
                  )}
                </div>

                {/* Includes */}
                <ul className="space-y-3 flex-1 mb-8">
                  {tier?.includes?.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                        className="shrink-0 mt-0.5"
                        style={{ color: tier?.featured ? 'var(--ceramic-blue)' : 'var(--aluminum-dim)' }}
                      >
                        <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span style={{ fontSize: '14px', color: 'rgba(212,212,216,0.8)', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.5 }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#book"
                  className={tier?.featured ? 'btn-primary w-full justify-center' : 'btn-secondary w-full justify-center'}
                  style={{ textAlign: 'center' }}
                >
                  {tier?.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Services */}
        <div className="mt-16 reveal reveal-delay-3">
          <div className="text-center mb-8">
            <h3
              className="font-manrope font-extrabold text-white"
              style={{ fontSize: 'clamp(20px, 3vw, 32px)', letterSpacing: '-0.02em' }}
            >
              Additional Services
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {additionalServices?.map((svc, i) => (
              <div
                key={svc?.name}
                className={`reveal reveal-delay-${(i % 3) + 1} rounded-2xl p-5 flex items-start justify-between gap-4`}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(63,63,70,0.6)' }}
              >
                <div>
                  <div
                    className="font-manrope font-semibold text-white"
                    style={{ fontSize: '14px', letterSpacing: '-0.01em' }}
                  >
                    {svc?.name}
                  </div>
                  {svc?.note && (
                    <div style={{ fontSize: '12px', color: 'var(--aluminum-dim)', marginTop: '3px' }}>
                      {svc?.note}
                    </div>
                  )}
                </div>
                <span
                  className="font-manrope font-extrabold shrink-0"
                  style={{ fontSize: '15px', color: 'var(--ceramic-blue)' }}
                >
                  {svc?.price}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* By Demand Services */}
        <div
          className="mt-10 reveal reveal-delay-4 rounded-2xl p-6 md:p-8 max-w-4xl mx-auto"
          style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(63,63,70,0.6)' }}
        >
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="shrink-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)' }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <div className="font-manrope font-bold text-white mb-1" style={{ fontSize: '16px' }}>
                By Demand
              </div>
              <div style={{ fontSize: '13px', color: 'var(--aluminum-dim)', marginBottom: '16px' }}>
                Pricing available after vehicle assessment
              </div>
              <div className="flex flex-wrap gap-2">
                {byDemandServices?.map((svc) => (
                  <span
                    key={svc}
                    style={{
                      fontSize: '12px',
                      fontFamily: 'DM Sans, sans-serif',
                      color: 'rgba(212,212,216,0.8)',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(63,63,70,0.8)',
                      borderRadius: '9999px',
                      padding: '5px 12px',
                      display: 'inline-block',
                    }}
                  >
                    {svc}
                  </span>
                ))}
              </div>
            </div>
            <a
              href="#book"
              className="btn-secondary shrink-0 self-start"
              style={{ whiteSpace: 'nowrap' }}
            >
              Get Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}